import * as Yup from 'yup';
import { Field, ErrorMessage, Formik } from 'formik';
import Swal from 'sweetalert2';
import { useListView } from '../../../component/view';
import { swlAlertMapping } from '../../../component/sweetAlert';
import { editRedem, redemPartnerUserList } from '../../api';
import { DrawerComponent } from '../../../../_metronic/assets/ts/components';
import clsx from 'clsx';
import { useQueryResponse } from '../../../component/response';
import { useEffect, useState } from 'react';
import { FullPageLoader } from '../../../component/helper';


/**
 * Validation schema for the EditRedem form using Yup.
 * Ensures required fields for points, TDS, logistic value, remarks, and conditional offer fields.
 */
const editUserSchema = Yup.object().shape({
    point: Yup.number().required('Points is required'),
    sku_tds: Yup.string().trim().required('TDS is required'),
    logistic_value: Yup.string().trim().required('logistic value is required'),
    is_free_product: Yup.string().oneOf(['yes', 'no']).required('Free product is required'),
    remarks: Yup.string().trim().required('Remarks is required'),
    offer_point: Yup.number()
        .nullable() // Allow null value if no special point
        .when('special_point', {
            is: (val) => !!val, // Check if special_point is truthy (not null, undefined, false, etc.)
            then: Yup.number().required('Offer point is required'), // Required when special_point is true
            otherwise: Yup.number().notRequired() // Optional otherwise
        }),

    // Conditional validation for from_date based on special_point
    from_date: Yup.string()
        .nullable()
        .when('special_point', {
            is: (val) => !!val, // Check if special_point is truthy
            then: Yup.string().required('From date is required '), // Required when special_point is true
            otherwise: Yup.string().notRequired() // Optional otherwise
        }),

    // Conditional validation for to_date based on special_point
    to_date: Yup.string()
        .nullable()
        .when('special_point', {
            is: (val) => !!val, // Check if special_point is truthy
            then: Yup.string().required('To date is required '), // Required when special_point is true
            otherwise: Yup.string().notRequired() // Optional otherwise
        }),
});
/**
 * EditRedemDrawerFrom component for editing redemption catalogue mapping in a drawer UI.
 * Handles form state, validation, submission, and data refresh.
 */
const EditRedemDrawerFrom = ({ data }) => {
    const { refetch } = useQueryResponse();
    const [redemPartner, setRedemPartner] = useState([])

    useEffect(() => {
        redemPartnerUserList('&rlId=12').then((d) => setRedemPartner(d.data))
    }, [])
    const initialValues = {
        logistic_type: data?.logistic_type,
        point: data?.point,
        status: data?.status,
        logistic_value: data.logistic_value,
        sku_tds: data?.sku_tds,
        is_free_product: data?.is_free_product ?? 'no',
        redem_catalogue_id: data.id,
        redemption_partner_id: data?.redemption_partner_id ?? "",
        offer_point: '',
        from_date: '',
        special_point: false,
        remarks: ""

    };

    /**
     * Handles form submission for editing redemption mapping.
     * Shows confirmation dialog and calls API.
     */
    const handleSubmit = async (values, actions) => {
        try {
            actions.setSubmitting(true);
            const confirmResult = await Swal.fire({
                title: 'Are you sure?',
                text: 'Are you sure you want to update?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-secondary'
                }
            });

            if (confirmResult.isConfirmed) {
                const response = await editRedem(values);
                swlAlertMapping(response);
                if (response.code !== 0) {
                    reFreshData(true)
                    setTimeout(() => {
                        DrawerComponent.hideAll();
                    }, 500);
                }

            }
        } catch (error) {
            console.error(error);
        } finally {
            actions.setSubmitting(false);
        }
    };

    /**
     * Refreshes data after successful update if requested.
     */
    const reFreshData = (withRefresh) => {
        if (withRefresh) {
            refetch()
        }
    }
    /**
     * Closes the drawer UI.
     */
    const drawerClose = () => {
        DrawerComponent.hideAll();
    }
    return (

        <Formik enableReinitialize initialValues={initialValues} validationSchema={editUserSchema} onSubmit={handleSubmit}>
            {({ values, handleSubmit, setSubmitting, setFieldValue, isSubmitting, handleChange }) => (
                <form className='w-100' onSubmit={handleSubmit}>
                    {isSubmitting && <FullPageLoader />}
                    <div style={{ border: '1px solid #E6E6E6' }} className="card">
                        <div className="card-header ps-4" style={{ minHeight: '39px' }}>
                            <div className="card-title fw-semibold fs-4 py-1">{'Redemption Catalouge'}</div>
                        </div>
                        <div className="card-body p-3 pt-3">
                            <div style={{ minHeight: '25px' }} className="card border  mt-4">
                                <div style={{ minHeight: '25px', background: '#f5e5e5' }} className="card-header p-2 ps-5 h-min-35px">
                                    <div className="card-title fs-6">Edit SKU Mapping By User Type </div>
                                </div>
                                <div className="card-body p-3">

                                    <div style={{ background: '#f5e5e5' }} className='p-4 mt-2 rounded'>
                                        <div className='d-flex flex-wrap gap-4'>

                                            {/* Logistic Type */}
                                            <div className='d-flex flex-column'>
                                                <label className="fw-semibold fs-6 required">Logistic type</label>
                                                <div className='d-flex  gap-3 mt-2'>
                                                    <div className="form-check form-check-inline">
                                                        <Field
                                                            type='radio'
                                                            onChange={() => { setFieldValue('logistic_type', 1) }}
                                                            name='logistic_type'
                                                            id='inlineenabled'
                                                            className={clsx('form-check-input')}
                                                            autoComplete='off'
                                                            value={1}
                                                            checked={values.logistic_type === 1}
                                                        />
                                                        <label className="form-check-label" htmlFor="inlineenabled">
                                                            <span className="fw-semibold fs-6 text-gray-700">Fixed Value</span>
                                                        </label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <Field
                                                            type='radio'
                                                            onChange={() => { setFieldValue('logistic_type', 2) }}
                                                            id='inlinedisabled'
                                                            name='logistic_type'
                                                            className={clsx('form-check-input')}
                                                            autoComplete='off'
                                                            value={2}
                                                            checked={values.logistic_type === 2}
                                                        />
                                                        <label className="form-check-label" htmlFor="inlinedisabled">
                                                            <span className="fw-semibold fs-6 text-gray-700">Percentage %</span>
                                                        </label>
                                                    </div>
                                                </div>
                                                <ErrorMessage name='logistic_type' component="div" className="text-danger mt-1" />
                                            </div>

                                            {/* Logistic Value */}
                                            <div className='d-flex flex-column'>
                                                <label className="fw-semibold fs-6 required">
                                                    Logistic {values.logistic_type === 1 ? 'Fixed' : '%'} value
                                                </label>
                                                <Field
                                                    name='logistic_value'
                                                    placeholder='value'
                                                    type='number'
                                                    className='form-control rounded-0'
                                                    onChange={handleChange}
                                                />
                                                <ErrorMessage name='logistic_value' component="div" className="text-danger mt-1" />
                                            </div>

                                            {/* TDS (appeared twice in your original code, so just once here) */}
                                            <div className='d-flex flex-column' >
                                                <label className="fw-semibold fs-6 required">TDS</label>
                                                <Field
                                                    name='sku_tds'
                                                    type='number'
                                                    className='form-control rounded-0'
                                                    onChange={handleChange}
                                                />
                                                <ErrorMessage name='sku_tds' component="div" className="text-danger mt-1" />
                                            </div>
                                            <div className='d-flex flex-column'>
                                                <label className="fw-semibold fs-6 required">Redemption Partner</label>
                                                <Field
                                                    as="select"
                                                    name="redemption_partner_id"
                                                    className="form-control form-select rounded-0"
                                                    onChange={handleChange}
                                                >
                                                    <option value="">IIRPL</option>
                                                    {redemPartner && redemPartner.map((partner) => (
                                                        <option key={partner?.id} value={partner?.id} className="text-capitalize">
                                                            {partner?.first_name} {partner?.last_name} {partner?.firm_name && `(${partner.firm_name})`}

                                                        </option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name="redemption_partner_id" component="div" className="text-danger mt-1" />
                                            </div>

                                            {/* Free Product */}
                                            <div className='d-flex flex-column'>
                                                <label className="fw-semibold fs-6 required">Is Free Product</label>
                                                <div className='d-flex gap-3 mt-2'>
                                                    <div className="form-check form-check-inline">
                                                        <Field
                                                            type='radio'
                                                            onChange={() => { setFieldValue('is_free_product', 'yes') }}
                                                            name='is_free_product'
                                                            id='editIsFreeProductYes'
                                                            className={clsx('form-check-input')}
                                                            autoComplete='off'
                                                            value='yes'
                                                            checked={values.is_free_product === 'yes'}
                                                        />
                                                        <label className="form-check-label" htmlFor="editIsFreeProductYes">
                                                            <span className="fw-semibold fs-6 text-gray-700">Yes</span>
                                                        </label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <Field
                                                            type='radio'
                                                            onChange={() => { setFieldValue('is_free_product', 'no') }}
                                                            id='editIsFreeProductNo'
                                                            name='is_free_product'
                                                            className={clsx('form-check-input')}
                                                            autoComplete='off'
                                                            value='no'
                                                            checked={values.is_free_product === 'no'}
                                                        />
                                                        <label className="form-check-label" htmlFor="editIsFreeProductNo">
                                                            <span className="fw-semibold fs-6 text-gray-700">No</span>
                                                        </label>
                                                    </div>
                                                </div>
                                                <ErrorMessage name='is_free_product' component="div" className="text-danger mt-1" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='border border-1 p-4 mt-5 rounded' style={{ backgroundColor: "#f6f6f6" }}>
                                        <div className='d-flex mb-3 gap-2' >
                                            <div className="col-md-2">
                                                <div className="row">
                                                    <label className="col-lg-12 col-form-label  fw-semibold fs-6">User Type</label>
                                                    <div className="col-md-12">
                                                        <span className='fw-semibold'>{data?.user_type?.type_name}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="row">
                                                    <label className="col-lg-12 col-form-label  fw-semibold fs-6">Product Name</label>
                                                    <div className="col-md-12">
                                                        <div className='d-flex flex-column'>
                                                            <span className='fw-semibold'>{data?.product_sku?.sku_name}</span>
                                                            <span className='fw-semibold'>{data?.product_sku?.sku_code}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-4">
                                                <div className="row">
                                                    <label className="col-lg-12 col-form-label required fw-semibold fs-6">Points</label>
                                                    <div className="col-md-12">
                                                        <Field
                                                            name={`point`}
                                                            placeholder={'point'}
                                                            type="text"
                                                            className="form-control rounded-0"
                                                            onChange={handleChange}
                                                            value={values?.point}
                                                        />
                                                        <ErrorMessage name={`point`} component="div" className="text-danger" />
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                        {/* Special Point Checkbox */}
                                        <div className="d-flex mb-3 gap-2">
                                            <div className="col-md-12">
                                                <div class="form-check form-check-inline">
                                                    <Field
                                                        name={`special_point`}
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        onChange={handleChange}
                                                        checked={values?.special_point}
                                                    />
                                                    <label class="form-check-label" for="inlineenabled"><span class="form-check-label ms-0 fw-semibold fs-6 text-danger">Do you want to add an offer point?</span>
                                                    </label>
                                                </div>

                                            </div>
                                        </div>
                                        {values?.special_point && (
                                            <div>
                                                {/* Special Point Minute */}
                                                <div className="d-flex mb-3 gap-2">
                                                    <div className="col-md-6">
                                                        <div className="row">
                                                            <label className="col-lg-4 col-form-label required fw-semibold fs-6">Offer Point</label>
                                                            <div className="col-md-8">
                                                                <Field
                                                                    name={`offer_point`}
                                                                    placeholder={'Offer Point'}
                                                                    type="number"
                                                                    className="form-control rounded-0"
                                                                    onChange={handleChange}
                                                                    value={values?.offer_point}
                                                                />
                                                                <ErrorMessage name={`offer_point`} component="div" className="text-danger" />
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>

                                                {/* From Date */}
                                                <div className="d-flex mb-3 gap-2">
                                                    <div className="col-md-6">
                                                        <div className="row">
                                                            <label className="col-lg-4 col-form-label required fw-semibold fs-6">From Date</label>
                                                            <div className="col-md-8">
                                                                <Field
                                                                    name={`from_date`}
                                                                    type="date"
                                                                    className="form-control rounded-0"
                                                                    onChange={handleChange}
                                                                    value={values?.from_date}
                                                                />
                                                                <ErrorMessage name={`from_date`} component="div" className="text-danger" />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* To Date */}
                                                    <div className="col-md-6">
                                                        <div className="row">
                                                            <label className="col-lg-4 col-form-label required fw-semibold fs-6">To Date</label>
                                                            <div className="col-md-8">
                                                                <Field
                                                                    name={`to_date`}
                                                                    type="date"
                                                                    className="form-control rounded-0"
                                                                    onChange={handleChange}
                                                                    value={values?.to_date}
                                                                />
                                                                <ErrorMessage name={`to_date`} component="div" className="text-danger" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                    <div className='border border-1 p-4 mt-5 rounded' style={{ backgroundColor: "#f6f6f6" }}>
                                        <div className="row">
                                            <div className="col-lg-6">
                                                <label className="col-lg-4 col-form-label required fw-semibold fs-6">Status</label>
                                                <div className="row">
                                                    <div className="col-lg-12 fv-row">
                                                        <div className="form-check form-check-inline">
                                                            <input

                                                                type='radio'
                                                                onChange={() => { setFieldValue('status', 1) }}
                                                                name='status'
                                                                id={'inlineenabled'}
                                                                className={clsx(
                                                                    'form-check-input',

                                                                )}
                                                                autoComplete='off'
                                                                value={1}
                                                                checked={values.status === 1}

                                                            />
                                                            <label className="form-check-label" for="inlineenabled">
                                                                <span className="form-check-label ms-0 fw-semibold fs-6 text-gray-700">Enabled</span>
                                                            </label>
                                                        </div>
                                                        <div className="form-check form-check-inline">
                                                            <input

                                                                type='radio'
                                                                onChange={() => { setFieldValue('status', 2) }}
                                                                id={'inlinedisabled'}
                                                                name='status'
                                                                className={clsx(
                                                                    'form-check-input',
                                                                )}
                                                                autoComplete='off'
                                                                value={2}
                                                                checked={values.status === 2}

                                                            />
                                                            <label className="form-check-label" for="inlinedisabled">
                                                                <span className="form-check-label ms-0 fw-semibold fs-6 text-gray-700">Disabled</span>
                                                            </label>
                                                        </div>
                                                        <ErrorMessage name={`status`} component="div" className="text-danger" />
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                        <div className="row">
                                            <label className="col-lg-4 col-form-label required fw-semibold required fs-6">Remarks</label>
                                            <div className="col-lg-12 fv-row">
                                                <textarea
                                                    onChange={handleChange}
                                                    name="remarks"
                                                    className="form-control form-control-lg form-control mb-lg-0 mb-3"
                                                    placeholder="Type Remarks"
                                                    value={values?.remarks}
                                                >
                                                    {values?.remarks}
                                                </textarea>
                                                <ErrorMessage name={`remarks`} component="div" className="text-danger" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-footer py-2">
                            <div className="d-flex gap-4 justify-content-end">
                                <button type="button" onClick={() => drawerClose()} id="kt_cancel" className="btn btn-dark">
                                    <span className="indicator-label">Cancel</span>
                                </button>

                                <button type="submit" id="kt_submit" className="btn btn-primary">
                                    <span className="indicator-label">{'Update'}</span>
                                    {isSubmitting && (
                                        <span className="indicator-progress">
                                            Please wait...
                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </span>
                                    )}
                                </button>

                            </div>
                        </div>
                    </div>
                </form>
            )}
        </Formik>

    );
};

/**
 * EditRedem component for triggering the edit redemption drawer for a row.
 */
const EditRedem = ({ row }) => {
    const { addPointRedem } = useListView()
    return <>
        <span
            id="kt_redem_edit_toggle"
            className="symbol symbol-25px cursor-pointer symbol-circle me-2"
            title="Edit catalogue"
            data-bs-toggle="tooltip"
            data-bs-placement="left"
            data-bs-dismiss="click"
            data-bs-trigger="hover"
            onClick={() => addPointRedem(row)}
        >
            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                <i className="bi bi-pencil-fill fs-4 text-white fw-semibold"></i>
            </span>
        </span>

    </>
}

/**
 * EditRedemDrawer component for rendering the edit redemption drawer UI.
 */
const EditRedemDrawer = () => {
    const { redemItem } = useListView()

    return <div
        id="kt_redem_edit"
        className="bg-body"
        data-kt-drawer="true"
        data-kt-drawer-name="redem"
        data-kt-drawer-activate="true"
        data-kt-drawer-overlay="true"
        data-kt-drawer-width="{default:'1000px', 'md': '1000px'}"
        data-kt-drawer-direction="end"
        data-kt-drawer-toggle="#kt_redem_edit_toggle"
        data-kt-drawer-close="#kt_redem_edit_close"
    >
        <EditRedemDrawerFrom data={redemItem} />
    </div>
}
export { EditRedem, EditRedemDrawer, EditRedemDrawerFrom }


